import { useEffect, useState, useCallback } from "react";
import {
  Loader2, Plus, MapPin, Clock, PlayCircle, CheckCircle2, XCircle, Navigation, Search,
  ArrowDownWideNarrow, ArrowUpNarrowWide, CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, rupiahShort, slug } from "@/lib/format";
import { Modal } from "@/components/Modal";
import { CustomerPickerDialog } from "@/components/CustomerPicker";

export default function Visits() {
  const { hasPerm } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createCustomer, setCreateCustomer] = useState(null);
  const [completeFor, setCompleteFor] = useState(null);
  const [recs, setRecs] = useState([]);
  const [recsLoading, setRecsLoading] = useState(true);
  const [recSort, setRecSort] = useState("desc");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/visits", { params: { status: statusFilter, q: q || undefined } }); setVisits(r.data.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [statusFilter, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const loadRecs = useCallback(async () => {
    setRecsLoading(true);
    try { const r = await api.get("/customers/dormant", { params: { oa_days: 30 } }); setRecs(r.data.data || []); }
    catch (e) { toast.error(apiError(e)); } finally { setRecsLoading(false); }
  }, []);
  useEffect(() => { loadRecs(); }, [loadRecs]);

  const sortedRecs = [...recs].sort((a, b) => (recSort === "desc" ? b.days_since - a.days_since : a.days_since - b.days_since));

  const scheduleFromRec = async (rec) => {
    try {
      const r = await api.get(`/customers/${rec.odoo_customer_id}`);
      setCreateCustomer(r.data.data); setCreateOpen(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const metrics = [
    { label: "Terjadwal", value: visits.filter((v) => v.status === "Terjadwal").length },
    { label: "Berlangsung", value: visits.filter((v) => v.status === "Berlangsung").length, active: true },
    { label: "Selesai", value: visits.filter((v) => v.status === "Selesai").length },
    { label: "Potensi Order", value: rupiahShort(visits.reduce((a, v) => a + (v.order_potential || 0), 0)) },
  ];

  const checkin = async (v) => {
    const send = async (lat, lng, acc) => {
      try { await api.post(`/visits/${v.id}/checkin`, { latitude: lat, longitude: lng, accuracy: acc }); toast.success("Check-in tercatat"); load(); }
      catch (e) { toast.error(apiError(e)); }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => send(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
        () => { toast.message("Izin GPS ditolak, memakai lokasi default"); send(-6.2, 106.816666, 0); },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    } else { send(-6.2, 106.816666, 0); }
  };

  const cancel = async (v) => {
    const reason = window.prompt("Alasan pembatalan?");
    if (!reason) return;
    try { await api.post(`/visits/${v.id}/cancel`, { reason }); toast.success("Kunjungan dibatalkan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="visits-page">
      <div className="section-head compact">
        <div><p className="eyebrow">LAPANGAN</p><h2>Kunjungan Sales</h2></div>
        {hasPerm("visits:create") && <button className="rj-btn rj-btn-primary" onClick={() => { setCreateCustomer(null); setCreateOpen(true); }} data-testid="open-create-visit-btn"><Plus size={16} /> Jadwalkan Kunjungan</button>}
      </div>

      <div className="visit-metrics">
        {metrics.map((m) => (
          <article key={m.label} className={m.active ? "active" : ""} data-testid={`visit-metric-${slug(m.label)}`}>
            <span>{m.label}</span><strong>{m.value}</strong>
          </article>
        ))}
      </div>

      <div className="panel visit-recs" data-testid="visit-recommendations">
        <div className="panel-head">
          <div>
            <h3>Rekomendasi Kunjungan</h3>
            <p>Outlet direct yang belum order &gt; 30 hari — prioritaskan kunjungan.</p>
          </div>
          <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => setRecSort((s) => (s === "desc" ? "asc" : "desc"))} data-testid="rec-sort-toggle">
            {recSort === "desc" ? <ArrowDownWideNarrow size={15} /> : <ArrowUpNarrowWide size={15} />}
            {recSort === "desc" ? "Terlama dulu" : "Tersingkat dulu"}
          </button>
        </div>
        {recsLoading ? (
          <div className="rj-loading"><Loader2 className="spin" /> Memuat rekomendasi…</div>
        ) : sortedRecs.length === 0 ? (
          <div className="rec-empty" data-testid="rec-empty">Semua outlet aktif — tidak ada rekomendasi kunjungan. 🎉</div>
        ) : (
          <div className="rec-list" data-testid="rec-list">
            {sortedRecs.map((r) => (
              <div className="rec-row" key={r.id} data-testid={`rec-row-${r.odoo_customer_id}`}>
                <div className="rec-main">
                  <strong>{r.name}</strong>
                  <span><MapPin size={13} /> {r.address || r.area || "-"}</span>
                </div>
                <div className="rec-meta">
                  <span className={`rec-days ${r.days_since >= 60 ? "danger" : "warn"}`} data-testid={`rec-days-${r.odoo_customer_id}`}>
                    {r.last_order_at ? `${r.days_since} hari tanpa order` : "Belum pernah order"}
                  </span>
                  <small>{r.last_order_at ? `Order terakhir ${r.last_order_at.slice(0, 10)}` : "—"}</small>
                </div>
                {hasPerm("visits:create") && (
                  <button className="rj-btn rj-btn-primary rj-btn-sm" onClick={() => scheduleFromRec(r)} data-testid={`rec-schedule-${r.odoo_customer_id}`}>
                    <CalendarPlus size={14} /> Jadwalkan
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="panel visit-board">
        <div className="table-tools">
          <div className="search-box"><Search /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari customer…" data-testid="visit-search" /></div>
          <select className="rj-select" style={{ width: 170 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="visit-status-filter">
            <option value="all">Semua Status</option><option>Terjadwal</option><option>Berlangsung</option><option>Selesai</option><option>Dibatalkan</option>
          </select>
        </div>
        {loading ? <div className="rj-loading"><Loader2 className="spin" /> Memuat kunjungan…</div> : visits.length === 0 ? (
          <div className="visit-empty"><MapPin /><h3>Belum ada kunjungan</h3><p>Jadwalkan kunjungan pertama Anda.</p></div>
        ) : (
          <div className="visit-list">
            {visits.map((v) => (
              <div className={`visit-row status-${slug(v.status)}`} key={v.id} data-testid={`visit-row-${v.id}`}>
                <div className="route-time"><Clock /> {fmtDateTime(v.scheduled_at)}</div>
                <div className="visit-main">
                  <strong>{v.customer_name}</strong>
                  <span><MapPin /> {v.address || v.address_name || "-"}</span>
                  <small>{v.purpose} · {v.owner_name}</small>
                  {v.outcome && <em>Hasil: {v.outcome}</em>}
                </div>
                <div className="visit-status-actions">
                  <span className={`visit-status ${slug(v.status)}`}>{v.status}</span>
                  <div>
                    {hasPerm("visits:update") && v.status === "Terjadwal" && (
                      <button className="rj-btn rj-btn-green rj-btn-sm" onClick={() => checkin(v)} data-testid={`visit-checkin-${v.id}`}><Navigation size={14} /> Check-in</button>
                    )}
                    {hasPerm("visits:update") && v.status === "Berlangsung" && (
                      <button className="rj-btn rj-btn-primary rj-btn-sm" onClick={() => setCompleteFor(v)} data-testid={`visit-complete-${v.id}`}><CheckCircle2 size={14} /> Selesai</button>
                    )}
                    {hasPerm("visits:update") && ["Terjadwal", "Berlangsung"].includes(v.status) && (
                      <button className="rj-btn rj-btn-danger rj-btn-sm" onClick={() => cancel(v)} data-testid={`visit-cancel-${v.id}`}><XCircle size={14} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {createOpen && <CreateVisit initialCustomer={createCustomer} onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); loadRecs(); }} />}
      {completeFor && <CompleteVisit visit={completeFor} onClose={() => setCompleteFor(null)} onSaved={() => { setCompleteFor(null); load(); }} />}
    </div>
  );
}

function CreateVisit({ onClose, onSaved, initialCustomer = null }) {
  const [customer, setCustomer] = useState(initialCustomer);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [purpose, setPurpose] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!customer) { toast.error("Pilih customer"); return; }
    if (!scheduledAt) { toast.error("Tentukan tanggal & jam"); return; }
    setSaving(true);
    const addr = (customer.delivery_addresses || [])[0];
    try {
      await api.post("/visits", {
        customer_odoo_id: customer.odoo_customer_id, customer_name: customer.name,
        shipping_partner_odoo_id: addr?.odoo_id, address_name: addr?.name, address: addr?.street,
        purpose: purpose || "Kunjungan rutin", scheduled_at: new Date(scheduledAt).toISOString(),
      });
      toast.success("Kunjungan dijadwalkan"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} className="visit-dialog" title="Jadwalkan Kunjungan" testId="create-visit-dialog"
      footer={<><button className="rj-btn rj-btn-ghost" onClick={onClose}>Batal</button>
        <button className="rj-btn rj-btn-primary" onClick={save} disabled={saving} data-testid="submit-visit-btn">{saving ? <Loader2 size={16} className="spin" /> : "Simpan Jadwal"}</button></>}>
      <div className="visit-form">
        <label className="span-2">Customer
          <button type="button" className={`customer-picker-trigger ${customer ? "selected" : ""}`} onClick={() => setPickerOpen(true)} data-testid="visit-pick-customer">
            <span className="customer-picker-icon"><MapPin size={18} /></span>
            {customer ? <span><strong>{customer.name}</strong><small>{customer.area}</small></span> : <span><strong>Pilih customer</strong><small>Klik untuk mencari</small></span>}
            <b>{customer ? "Ganti" : "Cari"}</b>
          </button>
        </label>
        <label className="span-2">Tujuan Kunjungan<input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="cth: Cek stok & tawarkan produk baru" data-testid="visit-purpose" /></label>
        <label className="span-2">Tanggal & Jam<input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="visit-schedule" /></label>
      </div>
      <CustomerPickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={setCustomer} />
    </Modal>
  );
}

function CompleteVisit({ visit, onClose, onSaved }) {
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [potential, setPotential] = useState(0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!outcome.trim()) { toast.error("Isi hasil kunjungan"); return; }
    setSaving(true);
    try { await api.post(`/visits/${visit.id}/complete`, { outcome, notes, order_potential: Number(potential) || 0 }); toast.success("Kunjungan selesai"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} className="visit-dialog" title="Selesaikan Kunjungan" subtitle={visit.customer_name} testId="complete-visit-dialog"
      footer={<><button className="rj-btn rj-btn-ghost" onClick={onClose}>Batal</button>
        <button className="rj-btn rj-btn-green" onClick={save} disabled={saving} data-testid="submit-complete-btn">{saving ? <Loader2 size={16} className="spin" /> : "Simpan & Check-out"}</button></>}>
      <div className="visit-form">
        <label className="span-2">Hasil Kunjungan<input value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="cth: Order diambil, restock" data-testid="complete-outcome" /></label>
        <label className="span-2">Catatan<textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        <label className="span-2">Potensi Order (Rp)<input type="number" value={potential} onChange={(e) => setPotential(e.target.value)} data-testid="complete-potential" /></label>
      </div>
    </Modal>
  );
}
